import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for synchronizing localStorage changes across browser tabs
 * @param {string} key - The localStorage key to monitor
 * @param {Function} onExternalChange - Callback when external changes are detected
 * @returns {Object} Methods to control internal change detection
 */
export function useStorageSync(key, onExternalChange) {
  const isInternalChange = useRef(false);

  const handleStorageChange = useCallback(
    (event) => {
      // Only process changes from other tabs for our specific key
      if (event.key === key && !isInternalChange.current && event.storageArea === localStorage) {
        try {
          const newValue = event.newValue ? JSON.parse(event.newValue) : null;
          const oldValue = event.oldValue ? JSON.parse(event.oldValue) : null;

          // Only trigger callback if there's an actual change
          if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
            onExternalChange(newValue, oldValue);
          }
        } catch (error) {
          console.error(`Error parsing storage event for key "${key}":`, error);
        }
      }
    },
    [key, onExternalChange]
  );

  /**
   * Mark that the next storage change is internal to prevent feedback loops
   */
  const setInternalChange = useCallback((isInternal) => {
    isInternalChange.current = isInternal;
  }, []);

  useEffect(() => {
    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleStorageChange]);

  return { setInternalChange };
}